import { API_VERSION, API_TIMEOUT_MS } from 'shared/consts'
import { Context } from 'koa'
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import curlirize from 'axios-curlirize/dist/curlirize'
import assert from 'shared/utils/assert'
import BackendError from 'shared/errors/BackendError'
import TimeoutError from 'shared/errors/TimeoutError'
import { pick } from 'shared/utils/objectUtils'
import loggerService, { Logger as ServerLogger } from 'shared/services/logger'
import { Logger as ClientLogger } from 'console-log-level'
import { BULLY_AUTH_HEADER_NAME } from 'shared/consts/authCookie'
import refreshAuthCookie from 'shared/utils/refreshAuthCookie'
import { X_FEATURE } from 'shared/consts/httpHeaders'
import deepmerge from 'deepmerge'
import { ServiceParameters } from './commonTypes/ServiceParameters'
import { Headers } from './commonTypes/IService'
import { SoftLoginExpiredError } from 'shared/errors/SoftLoginExpiredError'
import { ERROR_SENSITIVE_LOGIN_EXPIRED } from 'shared/consts/errorCodes'
import { IS_BROWSER } from 'shared/consts/env'
import { getMountPointsInstance } from 'shared/store-checkout/duck/common'

const stringifyRequest: (
  request: AxiosRequestConfig | AxiosResponse
) => string = (request: AxiosRequestConfig | AxiosResponse): string =>
  JSON.stringify(
    pick(request, ['headers', 'method', 'baseURL', 'url', 'params', 'data']),
    null,
    2
  )

abstract class APIService {
  protected baseURL: string
  protected apiVersion: string
  protected featureBranch: string
  protected client: AxiosInstance
  protected logger: ServerLogger | ClientLogger

  constructor({
    apiVersion = API_VERSION,
    authToken,
    baseURL,
    locale,
    contentTypeJSON = false,
    featureBranch,
    logger = loggerService,
    needsAuth,
    responseInterceptor: customResponseInterceptor = null
  }: ServiceParameters) {
    assert(baseURL, 'APIService: no "baseURL" option provided')
    assert(logger, 'APIService: no "logger" option provided')
    const params: AxiosRequestConfig = {
      baseURL,
      timeout: API_TIMEOUT_MS
    }

    const headers: any = {}

    if (needsAuth && authToken) {
      headers.Authorization = authToken
    }

    if (locale) {
      headers['Accept-Language'] = locale
    }

    if (contentTypeJSON) {
      headers.Accept = 'application/json'
      headers['Content-Type'] = 'application/json'
    }

    if (Object.keys(headers).length > 0) {
      params.headers = headers
    }

    const client: AxiosInstance = axios.create(params)

    if (process.env.DEBUG_AXIOS === 'true') {
      curlirize(client)
    }

    this.baseURL = baseURL
    this.apiVersion = apiVersion
    this.client = client
    this.logger = logger

    if (featureBranch) {
      this.featureBranch = featureBranch
    }

    const handleRequest = (request: AxiosRequestConfig): AxiosRequestConfig => {
      if (process.env.APP_ENV === 'development') {
        logger.debug(stringifyRequest(request), 'api:request')
      }
      return request
    }

    const handleResponse = (response: AxiosResponse): AxiosResponse => {
      if (process.env.APP_ENV === 'development') {
        logger.debug(stringifyRequest(response), 'api:response')
      }
      return response
    }

    this.client.interceptors.request.use(handleRequest)
    this.client.interceptors.response.use(handleResponse, error => {
      const { message } = error
      const isTimoutExceeded =
        /\btimeout\b.*\bexceeded\b|\bexceeded\b.*\btimeout\b/gi.test(message)

      if (isTimoutExceeded) {
        logger.error(`Timeout error intercepted: ${error.config?.url}`, error)
        throw new TimeoutError(error)
      }

      const isSoftLoginExpired =
        ERROR_SENSITIVE_LOGIN_EXPIRED === error.response?.data?.message

      if (isSoftLoginExpired) {
        const invalidParams = error.response.data?.invalidParams

        if (!invalidParams) {
          logger.error(
            'invalidParams key is not defined on soft login expired error'
          )
        }

        const email = invalidParams?.find(
          invalidParam => invalidParam.key === 'email'
        )

        if (!email) {
          logger.error('Email was not defined on soft login expired error')
        }

        if (IS_BROWSER) {
          const { location } = window
          const { pathname, search } = location

          return location.assign(
            getMountPointsInstance().getLogin({
              email: email?.field,
              forceLogin: true,
              redirect: `${pathname}${search}`
            })
          )
        }

        throw new SoftLoginExpiredError(
          error.response.data.message,
          email?.field
        )
      }

      throw new BackendError(error)
    })

    if (typeof customResponseInterceptor === 'function') {
      this.client.interceptors.response.use(customResponseInterceptor)
    }
  }

  private async request<T>(
    config: AxiosRequestConfig,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    const result: AxiosResponse<T> = await this.client.request<T>(
      deepmerge(config, this.prepareConfigFromContext(ctx))
    )

    if (ctx) {
      refreshAuthCookie(ctx, result.headers?.[BULLY_AUTH_HEADER_NAME])
    }

    return result
  }

  private prepareConfigFromContext(ctx: Context): AxiosRequestConfig | {} {
    const headers: Headers = {}

    if (this.featureBranch) {
      headers[X_FEATURE] = this.featureBranch
    }

    const authorization = ctx?.state?.authToken
    if (authorization) {
      headers.Authorization = authorization
    }

    if (Object.keys(headers).length === 0) {
      return headers
    }

    return { headers }
  }

  // eslint-disable-next-line max-params
  protected async get<T>(
    path: string,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.get(): Please provide a path.')

    return this.request<T>(
      {
        method: 'GET',
        url: path,
        ...options
      },
      ctx
    )
  }

  // eslint-disable-next-line max-params
  protected async post<B, T>(
    path: string,
    payload: B,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.post(): Please provide a path.')

    return this.request(
      {
        method: 'POST',
        url: path,
        data: payload,
        ...options
      },
      ctx
    )
  }

  protected sendBeacon<P>(path: string, payload: P): void {
    assert(path, 'APIService.sendBeacon(): Please provide a path.')
    assert(payload, 'APIService.sendBeacon(): Please provide a payload.')

    const blob: Blob = new Blob([JSON.stringify(payload)], {
      type: 'application/json;charset=UTF-8'
    }) // the blob is used to add the content type
    if (!navigator.sendBeacon) return

    navigator.sendBeacon(this.baseURL + path, blob)
  }

  // eslint-disable-next-line max-params
  protected async put<P, T>(
    path: string,
    payload: P,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.put(): Please provide a path.')
    assert(payload, 'APIService.put(): Please provide a payload.')

    return this.request(
      {
        method: 'PUT',
        url: path,
        data: payload,
        ...options
      },
      ctx
    )
  }

  // eslint-disable-next-line max-params
  protected async patch<P, T>(
    path: string,
    payload: P,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.patch(): Please provide a path.')
    assert(payload, 'APIService.patch(): Please provide a payload.')

    return this.request(
      {
        method: 'PATCH',
        url: path,
        data: payload,
        ...options
      },
      ctx
    )
  }

  // eslint-disable-next-line max-params
  protected async delete<T>(
    path: string,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.delete(): Please provide a path.')

    return this.request(
      {
        method: 'DELETE',
        url: path,
        ...options
      },
      ctx
    )
  }
}

export default APIService
